import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Typography
} from "@mui/material";
import { TableColumnsPicker } from "components/Documents/Report-sections-forms/TableColumnsPicker";
import {
  ColumnSettingsPopover,
  ColumnSettingsPopoverProps
} from "components/reports/ReportMeasuresTable/ColumnSettingsPopover";
import { calcFieldsToDisplay, DEAL_INDEX, indexNames } from "constants/reports";
import { useCharacteristics, usePrompt } from "hooks";
import { debounce, difference } from "lodash";
import { useAccountSelector, useDealActions, useMeasuresSelector } from "store";
import type { CardConfig, ReportTableColumn, TableCardConfig } from "store/types";
import { normalizeGroupByCharacteristic } from "utils";

import { SectionLabel } from "./SectionLabel";

interface Props {
  config: TableCardConfig;
  onUpdateConfig: (c: CardConfig) => void;
}

export const TableSettings: React.FC<Props> = ({ config, onUpdateConfig }) => {
  const { getMeasuresAction } = useDealActions();
  const measures = useMeasuresSelector();
  const { support_investor_groups } = useAccountSelector() || {};
  const characteristicsAll = useCharacteristics();
  const openPrompt = usePrompt();

  const [loadingMeasures, setLoadingMeasures] = useState(false);
  const [pmeMenuOpen, setPmeMenuOpen] = useState(false);
  const pmeChipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (measures.length === 0) {
      setLoadingMeasures(true);
      getMeasuresAction().finally(() => setLoadingMeasures(false));
    }
  }, [measures.length, getMeasuresAction]);

  const tableColumns = useMemo(
    () => ({
      measures: config.measures || [],
      characteristics: config.characteristics || [],
      calcFields: config.calculatedFields || []
    }),
    [config.measures, config.characteristics, config.calculatedFields]
  );
  const rawGroupBy = useMemo(() => config.rawGroupBy || ["none", "none"], [config.rawGroupBy]);
  const rowBy: "none" | "deal" | "investor" | "measure" = config.rowBy ? config.rowBy : "none";
  const indexNameValue = config.indexName;

  const selectedUniqueParameters = useMemo(
    () => [
      ...difference(tableColumns.calcFields, calcFieldsToDisplay),
      ...tableColumns.characteristics,
      ...tableColumns.measures
    ],
    [tableColumns]
  );

  const allSelectedColumns = useMemo(
    () => [...tableColumns.measures, ...tableColumns.calcFields, ...tableColumns.characteristics],
    [tableColumns]
  );

  const buildTableConfig = useCallback(
    (updates: {
      measures?: string[];
      calcFields?: string[];
      characteristics?: string[];
      groupBy?: string[];
      rowBy?: "deal" | "investor" | "measure" | "none";
      indexName?: string | undefined;
    }) => {
      const nextMeasures = updates.measures ?? tableColumns.measures;
      const nextCalcFields = updates.calcFields ?? tableColumns.calcFields;
      const nextCharacteristics = updates.characteristics ?? tableColumns.characteristics;
      const nextGroupBy = updates.groupBy ?? rawGroupBy;
      const nextRowBy: "none" | "deal" | "investor" | "measure" = updates.rowBy ?? rowBy;
      const nextIndexName = updates.indexName !== undefined ? updates.indexName : indexNameValue;

      const allColumns = [...nextMeasures, ...nextCalcFields, ...nextCharacteristics];
      const existingColumnMap = new Map((config.columns || []).map((col) => [col.aggregation, col]));
      const columnsForApi = allColumns.map((col) => existingColumnMap.get(col) || { aggregation: col });
      const allGroupBy = [
        ...nextGroupBy.filter((g) => g && g !== "none"),
        ...(nextRowBy !== "none" ? [nextRowBy] : [])
      ];

      const updatedConfig = {
        ...config,
        measures: nextMeasures,
        calculatedFields: nextCalcFields,
        characteristics: nextCharacteristics,
        columns: columnsForApi.length > 0 ? columnsForApi : undefined,
        groupBy: allGroupBy.map((g) => [g]),
        rawGroupBy: nextGroupBy,
        columnBy: "measure" as const,
        rowBy: nextRowBy === "none" ? undefined : nextRowBy,
        indexName: nextIndexName || undefined
      };

      onUpdateConfig(updatedConfig);
    },
    [config, tableColumns, rawGroupBy, rowBy, indexNameValue, onUpdateConfig]
  );

  const handleToggleCalcField = (param: string) => {
    if (param === "PME") {
      setPmeMenuOpen(true);
      return;
    }
    const nextCalcFields = tableColumns.calcFields.includes(param)
      ? tableColumns.calcFields.filter((p) => p !== param)
      : [...tableColumns.calcFields, param];
    buildTableConfig({ calcFields: nextCalcFields });
  };

  const debouncedBuildConfig = useMemo(() => debounce(buildTableConfig, 1000), [buildTableConfig]);

  const handleSelectIndex = (selectedIndex: string) => {
    setPmeMenuOpen(false);
    if (selectedIndex === "none") {
      debouncedBuildConfig({
        calcFields: tableColumns.calcFields.filter((p) => p !== "PME"),
        indexName: undefined
      });
    } else if (selectedIndex === DEAL_INDEX) {
      const nextCalcFields = tableColumns.calcFields.includes("PME")
        ? tableColumns.calcFields
        : [...tableColumns.calcFields, "PME"];
      debouncedBuildConfig({ calcFields: nextCalcFields, indexName: undefined });
    } else {
      const nextCalcFields = tableColumns.calcFields.includes("PME")
        ? tableColumns.calcFields
        : [...tableColumns.calcFields, "PME"];
      debouncedBuildConfig({ calcFields: nextCalcFields, indexName: selectedIndex });
    }
  };

  const handleOpenColumnsPicker = useCallback(
    async (event: React.MouseEvent<HTMLElement>) => {
      const newColumnsSet = await openPrompt({
        Component: TableColumnsPicker,
        componentType: "popover",
        anchorEl: event.currentTarget,
        props: {
          selectedColumns: {
            ...tableColumns,
            calcFields: difference(tableColumns.calcFields, calcFieldsToDisplay)
          }
        }
      });

      if (!newColumnsSet) return;

      buildTableConfig({
        characteristics: newColumnsSet.characteristics,
        measures: newColumnsSet.measures,
        calcFields: [
          ...calcFieldsToDisplay.filter((field) => tableColumns.calcFields.includes(field)),
          ...newColumnsSet.calcFields
        ]
      });
    },
    [openPrompt, tableColumns, buildTableConfig]
  );

  const [columnSettingsState, setColumnSettingsState] = useState<{
    open: boolean;
    isDuplicate: boolean;
    column?: ReportTableColumn;
    anchorEl?: HTMLElement | null;
  }>({ open: false, isDuplicate: false });

  const getColumnConfig = useCallback(
    (aggregation: string): ReportTableColumn => {
      return (config.columns || []).find((col) => col.aggregation === aggregation) || { aggregation };
    },
    [config.columns]
  );

  const handleColumnSettings: ColumnSettingsPopoverProps["onApply"] = useCallback(
    (queries) => {
      const col = columnSettingsState.column;
      setColumnSettingsState({ open: false, isDuplicate: false });
      if (!col) return;

      const currentColumns = config.columns || [];
      const updatedColumns = currentColumns.map((c) =>
        c.aggregation === col.aggregation
          ? queries
            ? { ...c, abbreviated: queries.abbreviated, decimal: queries.decimal }
            : { ...c, abbreviated: undefined, decimal: undefined }
          : c
      );

      onUpdateConfig({ ...config, columns: updatedColumns });
    },
    [columnSettingsState.column, config, onUpdateConfig]
  );

  const groupByOptions = useMemo(
    () => [
      { text: "None", value: "none" },
      { text: "Investor", value: "investor" },
      ...(support_investor_groups ? [{ text: "Investor Group", value: "investorGroup" }] : []),
      { text: "Deal", value: "deal" },
      { text: "Measure", value: "measure" },
      { text: "Month", value: "month" },
      { text: "Quarter", value: "quarter" },
      { text: "Year", value: "year" },
      ...characteristicsAll.map((char) =>
        typeof char === "string" ? { text: char, value: normalizeGroupByCharacteristic(char) } : char
      )
    ],
    [characteristicsAll, support_investor_groups]
  );

  const getGroupOptions = (currentIndex: number) => {
    const otherValue = currentIndex === 0 ? rawGroupBy[1] : rawGroupBy[0];
    return groupByOptions.filter((opt) => opt.value === "none" || opt.value !== otherValue);
  };

  return (
    <>
      <Box>
        <SectionLabel>Parameters</SectionLabel>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px", mb: 1 }}>
          {calcFieldsToDisplay.map((param) => (
            <Chip
              key={param}
              ref={param === "PME" ? pmeChipRef : undefined}
              label={param}
              size="small"
              onClick={() => handleToggleCalcField(param)}
              sx={{
                fontSize: 11,
                height: 24,
                cursor: "pointer",
                bgcolor: tableColumns.calcFields.includes(param) ? "#c3c9cf" : undefined,
                "&:hover": { bgcolor: "#e2e8f0" }
              }}
            />
          ))}
          <Menu anchorEl={pmeChipRef.current} open={pmeMenuOpen} onClose={() => setPmeMenuOpen(false)}>
            <MenuItem onClick={() => handleSelectIndex("none")} sx={{ fontSize: 12 }}>
              None
            </MenuItem>
            <MenuItem
              selected={!indexNameValue && tableColumns.calcFields.includes("PME")}
              onClick={() => handleSelectIndex(DEAL_INDEX)}
              sx={{ fontSize: 12 }}
            >
              Deal specific index
            </MenuItem>
            {Object.entries(indexNames).map(([index, name]) => (
              <MenuItem
                key={index}
                selected={index === indexNameValue}
                onClick={() => handleSelectIndex(index)}
                sx={{ fontSize: 12 }}
              >
                {name}
              </MenuItem>
            ))}
          </Menu>
          {selectedUniqueParameters.length > 0 && (
            <Chip
              label={`(${selectedUniqueParameters.length})`}
              size="small"
              sx={{ fontSize: 11, height: 24, bgcolor: "#c3c9cf" }}
            />
          )}
        </Box>
        {loadingMeasures ? (
          <CircularProgress size={16} />
        ) : (
          <Button
            size="sm"
            onClick={handleOpenColumnsPicker}
            sx={{ fontSize: 11, textTransform: "none", color: "#26A6AE", p: "2px 6px" }}
          >
            More columns...
          </Button>
        )}
      </Box>

      {allSelectedColumns.length > 0 && (
        <Box>
          <SectionLabel>Selected Parameters</SectionLabel>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
            {allSelectedColumns.map((param) => (
              <Box
                key={param}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  py: "2px",
                  px: "4px",
                  borderRadius: "4px",
                  "&:hover": { bgcolor: "#f5f5f5" }
                }}
              >
                <Typography sx={{ fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {param}
                </Typography>
                <IconButton
                  size="sm"
                  onClick={(e) =>
                    setColumnSettingsState({
                      open: true,
                      column: getColumnConfig(param),
                      isDuplicate: false,
                      anchorEl: e.currentTarget
                    })
                  }
                  sx={{ p: "2px", flexShrink: 0 }}
                >
                  <SettingsOutlinedIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            ))}
          </Box>
          <ColumnSettingsPopover
            open={columnSettingsState.open}
            anchorEl={columnSettingsState.anchorEl}
            column={columnSettingsState.column}
            isDuplicate={false}
            onClose={() => setColumnSettingsState({ open: false, isDuplicate: false })}
            onApply={handleColumnSettings}
          />
        </Box>
      )}

      <Box>
        <SectionLabel>Group By</SectionLabel>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <FormControl size="small" fullWidth>
            <InputLabel sx={{ fontSize: 12 }}>Group 1</InputLabel>
            <Select
              value={rawGroupBy[0]}
              label="Group 1"
              onChange={(e) => buildTableConfig({ groupBy: [e.target.value, rawGroupBy[1]] })}
              IconComponent={(props) => <KeyboardArrowDownIcon {...props} sx={{ fontSize: 18, color: "#6B7280" }} />}
              sx={{
                fontSize: 12,
                "& .MuiSelect-icon": { right: 10, color: "#6B7280" }
              }}
            >
              {getGroupOptions(0).map((opt) => (
                <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 12 }}>
                  {opt.text}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel sx={{ fontSize: 12 }}>Group 2</InputLabel>
            <Select
              value={rawGroupBy[1]}
              label="Group 2"
              onChange={(e) => buildTableConfig({ groupBy: [rawGroupBy[0], e.target.value] })}
              IconComponent={(props) => <KeyboardArrowDownIcon {...props} sx={{ fontSize: 18, color: "#6B7280" }} />}
              sx={{
                fontSize: 12,
                "& .MuiSelect-icon": { right: 10, color: "#6B7280" }
              }}
            >
              {getGroupOptions(1).map((opt) => (
                <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 12 }}>
                  {opt.text}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Box>
        <SectionLabel>Last Group (Row By)</SectionLabel>
        <FormControl size="small" fullWidth>
          <InputLabel sx={{ fontSize: 12 }}>Last Group</InputLabel>
          <Select
            value={rowBy}
            label="Last Group"
            onChange={(e) =>
              buildTableConfig({
                rowBy: e.target.value as "none" | "deal" | "investor" | "measure"
              })
            }
            IconComponent={(props) => <KeyboardArrowDownIcon {...props} sx={{ fontSize: 18, color: "#6B7280" }} />}
            sx={{
              fontSize: 12,
              "& .MuiSelect-icon": { right: 10, color: "#6B7280" }
            }}
          >
            <MenuItem value="none" sx={{ fontSize: 12 }}>
              None
            </MenuItem>
            <MenuItem value="deal" sx={{ fontSize: 12 }}>
              Deal
            </MenuItem>
            <MenuItem value="investor" sx={{ fontSize: 12 }}>
              Investor
            </MenuItem>
            <MenuItem value="measure" sx={{ fontSize: 12 }}>
              Measure
            </MenuItem>
          </Select>
        </FormControl>
      </Box>
    </>
  );
};
